import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import type { LAYER_CONFIG } from 'js/model/surveys/SurveyLayerConfig';
import ListItem from '@mui/material/ListItem';
import { ListItemIcon, ListItemText } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import RadioButtonUncheckedOutlined from '@mui/icons-material/RadioButtonUncheckedOutlined';
import RadioButtonCheckedOutlined from '@mui/icons-material/RadioButtonCheckedOutlined';
import { getLayerConfig } from 'js/components/Prescription/PrescriptionUtils';
import { CheckBoxOutlineBlankOutlined, CheckBoxOutlined } from '@mui/icons-material';
import { voidFunc } from 'js/constants/PropTypeUtils';
import useShiftKeyDown from 'js/hooks/useShiftKeyDown';
import { useLangFile } from 'js/context/LanguageContext';
import { getSourceCategory } from '../../helpers/SurveyUtils';
import { SOURCE_CATEGORY } from '../Prescription/PrescriptionJob';

const MapLegendLayerListItem = (props: MapLegendLayerListItem.propTypes) => {
  const config: LAYER_CONFIG = getLayerConfig(props.layer);
  const shiftDown = useShiftKeyDown();
  const LangFile = useLangFile();

  const handleOnClick = useCallback(() => {
    if (shiftDown && props.enableMultipleSelect) {
      props.onCheckboxClick(props.layer);
    } else {
      props.onClick(props.layer);
    }
  }, [props.layer, props.onClick, shiftDown, props.enableMultipleSelect, props.onCheckboxClick]);

  const handleOnCheckboxClicked = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (props.enableMultipleSelect) {
        props.onCheckboxClick(props.layer);
      } else {
        props.onClick(props.layer);
      }
    },
    [props.layer, props.enableMultipleSelect, props.onClick, props.onCheckboxClick]
  );

  if (!config) {
    return null;
  }

  const icon = props.enableMultipleSelect ? (
    <CheckBoxOutlineBlankOutlined fontSize="small" />
  ) : (
    <RadioButtonUncheckedOutlined fontSize="small" />
  );
  const checkedIcon = props.enableMultipleSelect ? (
    <CheckBoxOutlined fontSize="small" />
  ) : (
    <RadioButtonCheckedOutlined fontSize="small" />
  );

  let title = config.getName(LangFile);
  const category = getSourceCategory(props.layer);
  if (category === SOURCE_CATEGORY.VITALITY) {
    const shortHand = config.getShorthand(LangFile);
    if (shortHand) {
      title = title + ` (${shortHand})`;
    }
  }

  return (
    <ListItem
      data-testid={`map-layer-${props.layer}`}
      dense={true}
      button={true}
      disabled={props.disabled}
      onClick={handleOnClick}
      key={props.layer}
    >
      {!props.disableCheckbox && (
        <ListItemIcon style={{ minWidth: 36 }}>
          <Checkbox
            style={{ padding: 0 }}
            checked={props.isSelected}
            onClick={handleOnCheckboxClicked}
            icon={icon}
            checkedIcon={checkedIcon}
            color="primary"
          />
        </ListItemIcon>
      )}
      <ListItemText primary={title} />
      {props.renderListItemSecondayAction && props.renderListItemSecondayAction()}
    </ListItem>
  );
};

MapLegendLayerListItem.propTypes = {
  layer: PropTypes.string,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  onCheckboxClick: PropTypes.func,
  disabled: PropTypes.bool,
  disableCheckbox: PropTypes.bool,
  enableMultipleSelect: PropTypes.bool,

  elevated: PropTypes.bool,
  colorStrategy: PropTypes.object,
  title: PropTypes.string,
  renderListItemSecondayAction: PropTypes.func,
};

MapLegendLayerListItem.defaultProps = {
  onClick: voidFunc,
  onCheckboxClick: voidFunc,
};

export default memo(MapLegendLayerListItem);
